import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil, Save, X, Users } from "lucide-react";

const FIELDS = ["melayu_l", "melayu_p", "cina_l", "cina_p", "india_l", "india_p"];

const sumL = (r) => (r.melayu_l || 0) + (r.cina_l || 0) + (r.india_l || 0);
const sumP = (r) => (r.melayu_p || 0) + (r.cina_p || 0) + (r.india_p || 0);
const show = (n) => (n ? n : "");

export default function EnrolmentTable({ canEdit }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/enrolment");
      setData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load enrolment table");
    }
  };
  useEffect(() => { load(); }, []);

  const startEdit = () => {
    setDraft({ date_label: data.date_label, rows: data.rows.map((r) => ({ ...r })) });
    setEditing(true);
  };
  const cancel = () => { setDraft(null); setEditing(false); };

  const setCell = (rowIndex, field, value) => {
    const n = Math.max(0, parseInt(value, 10) || 0);
    setDraft((d) => ({ ...d, rows: d.rows.map((r, i) => (i === rowIndex ? { ...r, [field]: n } : r)) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/enrolment", {
        date_label: draft.date_label,
        rows: draft.rows.map((r) => ({
          form: r.form,
          melayu_l: r.melayu_l || 0, melayu_p: r.melayu_p || 0,
          cina_l: r.cina_l || 0, cina_p: r.cina_p || 0,
          india_l: r.india_l || 0, india_p: r.india_p || 0,
        })),
      });
      setData(res.data);
      setDraft(null);
      setEditing(false);
      toast.success("Enrolment table updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save enrolment table");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const view = editing ? draft : data;

  const totals = view.rows.reduce(
    (acc, r) => {
      FIELDS.forEach((f) => { acc[f] += r[f] || 0; });
      return acc;
    },
    { melayu_l: 0, melayu_p: 0, cina_l: 0, cina_p: 0, india_l: 0, india_p: 0 },
  );
  const grandL = totals.melayu_l + totals.cina_l + totals.india_l;
  const grandP = totals.melayu_p + totals.cina_p + totals.india_p;

  const cellInput = (rowIndex, field, value) => (
    <input
      type="number"
      min="0"
      value={value || value === 0 ? value : 0}
      onChange={(e) => setCell(rowIndex, field, e.target.value)}
      className="w-14 rounded-md border border-border bg-background px-1.5 py-1 text-center text-sm focus:outline-none focus:ring-2 focus:ring-primary"
      data-testid={`enrolment-cell-${rowIndex}-${field}`}
      aria-label={`${view.rows[rowIndex].form} ${field}`}
    />
  );

  const num = "border border-border px-2 py-2 text-center tabular-nums";
  const head = "border border-border bg-[#1E293B] px-2 py-2 text-center text-xs font-bold uppercase tracking-wide text-white";

  return (
    <section className="space-y-4 border-t-4 border-sky-500 pt-4" data-testid="enrolment-section" aria-labelledby="enrolment-title">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-primary">
            <Users className="h-4 w-4" /> Enrolmen Penghuni Asrama
          </p>
          <h2 id="enrolment-title" className="mt-1 font-display text-xl font-bold">Student&apos;s Statistics</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            SMK Mutiara Impian · Penang Sports School —{" "}
            {editing ? (
              <input
                value={draft.date_label}
                onChange={(e) => setDraft((d) => ({ ...d, date_label: e.target.value }))}
                className="inline-block w-28 rounded-md border border-border bg-background px-2 py-0.5 text-sm"
                data-testid="enrolment-date-input"
                aria-label="Tarikh"
              />
            ) : (
              <span className="font-semibold text-foreground">Tarikh: {data.date_label}</span>
            )}
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            {editing ? (
              <>
                <Button variant="outline" onClick={cancel} data-testid="enrolment-cancel-btn"><X className="h-4 w-4" /> Batal</Button>
                <Button onClick={save} disabled={saving} data-testid="enrolment-save-btn"><Save className="h-4 w-4" /> {saving ? "Menyimpan…" : "Simpan"}</Button>
              </>
            ) : (
              <Button variant="outline" onClick={startEdit} data-testid="enrolment-edit-btn"><Pencil className="h-4 w-4" /> Edit jadual</Button>
            )}
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse text-sm" data-testid="enrolment-table">
          <thead>
            <tr>
              <th rowSpan={2} className={`${head} text-left`}>TINGKATAN</th>
              <th colSpan={2} className={head}>MELAYU</th>
              <th colSpan={2} className={head}>CINA</th>
              <th colSpan={2} className={head}>INDIA</th>
              <th colSpan={2} className={head}>JUMLAH</th>
              <th rowSpan={2} className={head}>JUMLAH</th>
            </tr>
            <tr>
              {["L", "P", "L", "P", "L", "P", "L", "P"].map((g, i) => (
                <th key={i} className={head}>{g}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.rows.map((r, i) => (
              <tr key={r.form} data-testid={`enrolment-row-${r.form.replace(/\s/g, "-")}`}>
                <th scope="row" className="border border-border bg-[#0F172A] px-3 py-2 text-left text-xs font-bold uppercase text-white">{r.form}</th>
                {FIELDS.map((f) => (
                  <td key={f} className={num}>
                    {editing ? cellInput(i, f, r[f]) : show(r[f])}
                  </td>
                ))}
                <td className={`${num} font-semibold bg-muted/40`}>{show(sumL(r))}</td>
                <td className={`${num} font-semibold bg-muted/40`}>{show(sumP(r))}</td>
                <td className={`${num} font-bold bg-muted/60`}>{show(sumL(r) + sumP(r))}</td>
              </tr>
            ))}
            <tr className="font-bold" data-testid="enrolment-row-JUMLAH">
              <th scope="row" className="border border-border bg-[#0F172A] px-3 py-2 text-left text-xs font-bold uppercase text-white">JUMLAH</th>
              {FIELDS.map((f) => (
                <td key={f} className={`${num} bg-muted/40`}>{show(totals[f])}</td>
              ))}
              <td className={`${num} bg-muted/60`}>{show(grandL)}</td>
              <td className={`${num} bg-muted/60`}>{show(grandP)}</td>
              <td className={`${num} bg-primary/15`}>{show(grandL + grandP)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {canEdit && !editing && (
        <p className="text-xs text-muted-foreground">Admins, Wardens and Hostel Supervisors can edit the enrolment figures; the JUMLAH columns and row total are calculated automatically.</p>
      )}
    </section>
  );
}
